import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Empty,
  Skeleton,
  Space,
  Tag,
  theme as antTheme,
  Typography,
} from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import { Stakeholder } from '@totem/components/accountPlan/types';
import {
  initials,
  RELATIONSHIP_COLORS,
  SENTIMENT,
} from '@totem/components/accountPlan/utilities';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';

import './orgChart.css';

const ACCOUNT_PLAN_ROUTE = '/dashboard/admin/accountPlan';

type OrgNode = {
  stakeholder: Stakeholder;
  reports: OrgNode[];
};

const byName = (left: Stakeholder, right: Stakeholder) =>
  left.name.localeCompare(right.name);

// Roots are stakeholders with no manager, or whose manager is no longer on the plan.
const buildTree = (stakeholders: Stakeholder[]): OrgNode[] => {
  const ids = new Set(stakeholders.map((stakeholder) => stakeholder.id));
  const placed = new Set<string>();

  const toNode = (stakeholder: Stakeholder): OrgNode => {
    placed.add(stakeholder.id);
    return {
      stakeholder,
      reports: stakeholders
        .filter(
          (report) =>
            report.reportsTo === stakeholder.id && !placed.has(report.id),
        )
        .sort(byName)
        .map(toNode),
    };
  };

  return stakeholders
    .filter(
      (stakeholder) =>
        !stakeholder.reportsTo || !ids.has(stakeholder.reportsTo),
    )
    .sort(byName)
    .map(toNode);
};

export const OrgChartExtra = () => {
  const { data, loading, onRefresh } = useContext(AccountPlanContext);

  return (
    <Space>
      <RecordCountBadge count={data?.plan.stakeholders.length ?? 0} />
      <Link to={ACCOUNT_PLAN_ROUTE}>
        <Button>Edit stakeholders</Button>
      </Link>
      <Button
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={onRefresh}
        title="Refresh"
      />
    </Space>
  );
};

const NodeCard = ({ stakeholder }: { stakeholder: Stakeholder }) => {
  const { token } = antTheme.useToken();

  return (
    <div
      styleName="card"
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Avatar style={{ background: token.colorPrimary, flexShrink: 0 }}>
        {initials(stakeholder.name)}
      </Avatar>
      <div styleName="card-body">
        <Typography.Text strong>{stakeholder.name}</Typography.Text>
        {stakeholder.role && (
          <Typography.Text type="secondary" styleName="card-line">
            {stakeholder.role}
          </Typography.Text>
        )}
        <Space size={4} wrap styleName="card-line">
          <Tag
            color={RELATIONSHIP_COLORS[stakeholder.relationship] ?? 'default'}
          >
            {stakeholder.relationship || 'Unknown'}
          </Tag>
          {stakeholder.orgType && stakeholder.orgType !== 'Customer' && (
            <Tag>{stakeholder.orgType}</Tag>
          )}
          {SENTIMENT[stakeholder.sentiment] ?? ''}
        </Space>
      </div>
    </div>
  );
};

const Branch = ({ node }: { node: OrgNode }) => (
  <li styleName="node">
    <NodeCard stakeholder={node.stakeholder} />
    {node.reports.length > 0 && (
      <ul styleName="reports">
        {node.reports.map((report) => (
          <Branch key={report.stakeholder.id} node={report} />
        ))}
      </ul>
    )}
  </li>
);

const OrgChartLayout = () => {
  const { data, loading } = useContext(AccountPlanContext);
  const { token } = antTheme.useToken();

  if (!data) {
    return <Skeleton active loading={loading} />;
  }

  const roots = buildTree(data.plan.stakeholders);

  if (roots.length === 0) {
    return (
      <Empty description="No stakeholders on this organization's account plan yet.">
        <Link to={ACCOUNT_PLAN_ROUTE}>
          <Button type="primary">Add stakeholders</Button>
        </Link>
      </Empty>
    );
  }

  return (
    <>
      <Typography.Paragraph type="secondary">
        Reporting lines come from each stakeholder&apos;s &quot;Reports to&quot;
        on the Account Plan. People with no manager are shown at the top.
      </Typography.Paragraph>
      <div
        styleName="chart"
        style={{ '--org-chart-line': token.colorBorder } as React.CSSProperties}
      >
        <ul styleName="roots">
          {roots.map((root) => (
            <Branch key={root.stakeholder.id} node={root} />
          ))}
        </ul>
      </div>
    </>
  );
};

export default OrgChartLayout;
